<script setup>
/**
 * "What happens here?" — four kinds, each with the one sentence that tells them apart.
 *
 * NOT A DROPDOWN, AND NOT A MODAL. `TaskStepTypePicker.vue`'s markup, verbatim in shape:
 * a list of rows, one sentence each, opening IN PLACE at the position the step will go.
 * "Add a step here" is a question about a position, and a dialog in the middle of the
 * screen throws that away. It is also the reason nothing here teleports — `Dialog`,
 * `Popover` and `SidePanel` all reach `TeleportWithDirection` → `useMapGetter`, which
 * throws inside a render effect AFTER a test has resolved, so it can only ever fail an
 * exit code. It did, on both environments.
 *
 * ── FOUR ROWS, NOT SEVEN ──────────────────────────────────────────────────────────────
 * `ask`, `choose` and `choose_live` were never three ideas — they are three answers to
 * "where does the answer come from?", and they collapse into one Question whose eight-row
 * picker lives on the step screen (`AnswerKindPicker.vue`). Their names were also the two
 * hardest in the product to tell apart, which `TaskStepTypePicker.vue`'s own docblock
 * admits.
 *
 * AND `end` IS NOT A KIND. The end of the list is the end of the task. An existing `end`
 * step still round-trips exactly — `helpers/task-spine.js` reads it back as a row whose
 * "then" is Stop — but there is nothing to add here called "Finish", because adding a step
 * that means "stop" to the bottom of a list that already stops is a concept with no work
 * to do.
 */
import Button from 'dashboard/components-next/button/Button.vue';

import { useWasloI18n } from '../../i18n.js';
import { STEP_KINDS } from '../../helpers/task-spine-edit.js';

const emit = defineEmits(['pick', 'cancel']);

const { t } = useWasloI18n();

const key = name => `WASLO.TASKS.COMPOSE.KIND.${name}`;

const ICONS = {
  question: 'i-lucide-message-circle-question',
  tell: 'i-lucide-megaphone',
  call: 'i-lucide-send',
  handoff: 'i-lucide-user-round',
};

const label = kind => t(key(`${kind.toUpperCase()}_LABEL`));
const about = kind => t(key(`${kind.toUpperCase()}_ABOUT`));
const icon = kind => ICONS[kind] ?? 'i-lucide-circle';
</script>

<template>
  <div
    class="flex flex-col gap-3 p-4 outline outline-1 rounded-xl outline-n-weak bg-n-solid-1"
    data-waslo-kind-picker
  >
    <p class="mb-0 text-sm font-medium text-n-slate-12">
      {{ t(key('HEADING')) }}
    </p>
    <ul class="flex flex-col gap-1 mb-0">
      <li v-for="kind in STEP_KINDS" :key="kind">
        <button
          type="button"
          class="flex items-start w-full gap-3 p-3 text-start rounded-lg hover:bg-n-alpha-2"
          :data-waslo-kind-pick="kind"
          @click="emit('pick', kind)"
        >
          <span
            class="mt-0.5 shrink-0 size-4 text-n-slate-11"
            :class="icon(kind)"
            aria-hidden="true"
          />
          <span class="flex flex-col min-w-0 gap-0.5">
            <span class="text-sm font-medium text-n-slate-12">
              {{ label(kind) }}
            </span>
            <span class="text-sm text-n-slate-11">{{ about(kind) }}</span>
          </span>
        </button>
      </li>
    </ul>
    <div class="flex">
      <Button
        :label="t(key('CANCEL'))"
        variant="faded"
        color="slate"
        size="sm"
        data-waslo-kind-cancel
        @click="emit('cancel')"
      />
    </div>
  </div>
</template>
